import { apiSlice } from "../../../features/api/apiSlice";

export type Department = {
  id: number;
  name: string;
  code?: string | null;
  description?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
};

function pickData(res: any) {
  // supports raw arrays/models or {data: ...}
  return res?.data?.data ?? res?.data ?? res;
}

export const departmentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ LIST
    getDepartments: builder.query<Department[], void>({
      query: () => "/departments",
      transformResponse: (res: any) => pickData(res) || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((d) => ({
                type: "Departments" as const,
                id: d.id,
              })),
              { type: "Departments" as const, id: "LIST" },
            ]
          : [{ type: "Departments" as const, id: "LIST" }],
    }),

    // ✅ CREATE
    createDepartment: builder.mutation<Department, Partial<Department>>({
      query: (body) => ({
        url: "/departments",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Departments" as const, id: "LIST" }],
    }),

    // ✅ DETAILS
    getDepartment: builder.query<Department, number>({
      query: (id) => `/departments/${id}`,
      transformResponse: (res: any) => pickData(res),
      providesTags: (_res, _err, id) => [{ type: "Departments" as const, id }],
    }),

    // ✅ UPDATE
    updateDepartment: builder.mutation<
      Department,
      { id: number; data: Partial<Department> }
    >({
      query: ({ id, data }) => ({
        url: `/departments/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Departments" as const, id },
        { type: "Departments" as const, id: "LIST" },
      ],
    }),

    // ✅ DELETE
    deleteDepartment: builder.mutation<{ message?: string }, number>({
      query: (id) => ({
        url: `/departments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Departments" as const, id: "LIST" }],
    }),

    // ✅ OPTIONAL: toggle active (only if you add backend route)
    toggleDepartment: builder.mutation<Department, number>({
      query: (id) => ({
        url: `/departments/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Departments" as const, id },
        { type: "Departments" as const, id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useGetDepartmentQuery,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useToggleDepartmentMutation,
} = departmentsApi;
